import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Select, MenuItem, FormControl, InputLabel, Chip,
  CircularProgress, Snackbar, Alert, Switch, FormControlLabel, Avatar, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  ArrowBack as BackIcon, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  PersonAdd as PersonAddIcon, DirectionsCar, PointOfSale, AdminPanelSettings,
  Person, Restaurant, LocalDining, Star,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { getFirestore, collection, getDocs, doc, setDoc, deleteDoc } from '../../services/firestoreCompat';

interface UserRecord {
  id: string;
  name: string;
  email: string;
  role: string;
  active: boolean;
  pin?: string;
  createdAt?: string;
  source?: string;
}

const ROLE_OPTIONS = [
  { value: 'owner', label: 'Owner', labelDe: 'Inhaber', icon: <Star sx={{ fontSize: 16 }} />, color: '#F44336' },
  { value: 'restaurant_admin', label: 'Restaurant Admin', labelDe: 'Restaurant-Admin', icon: <AdminPanelSettings sx={{ fontSize: 16 }} />, color: '#FF9800' },
  { value: 'admin', label: 'Admin', labelDe: 'Admin', icon: <AdminPanelSettings sx={{ fontSize: 16 }} />, color: '#2196F3' },
  { value: 'cashier', label: 'Cashier', labelDe: 'Kassierer', icon: <PointOfSale sx={{ fontSize: 16 }} />, color: '#00C896' },
  { value: 'waiter', label: 'Waiter', labelDe: 'Kellner', icon: <Restaurant sx={{ fontSize: 16 }} />, color: '#9C27B0' },
  { value: 'cook', label: 'Cook', labelDe: 'Koch', icon: <LocalDining sx={{ fontSize: 16 }} />, color: '#FF5722' },
  { value: 'driver', label: 'Driver', labelDe: 'Fahrer', icon: <DirectionsCar sx={{ fontSize: 16 }} />, color: '#00BCD4' },
  { value: 'customer', label: 'Customer', labelDe: 'Kunde', icon: <Person sx={{ fontSize: 16 }} />, color: '#6b7280' },
];

const getRoleConfig = (role: string) => ROLE_OPTIONS.find(r => r.value === role) || ROLE_OPTIONS[7];

const UserManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserRecord | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserRecord | null>(null);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const [formData, setFormData] = useState({ name: '', email: '', role: 'cashier', active: true, pin: '' });
  const [filterRole, setFilterRole] = useState<string>('all');

  const loadUsers = useCallback(async () => {
    try {
      const db = getFirestore();
      const userList: UserRecord[] = [];
      const seenEmails = new Set<string>();

      // 1. POS users
      try {
        const snap = await getDocs(collection(db, 'pos_users'));
        snap.forEach((d) => {
          const data = d.data();
          const email = data.email || '';
          if (email && !seenEmails.has(email.toLowerCase())) {
            seenEmails.add(email.toLowerCase());
            userList.push({ id: d.id, name: data.name || email, email, role: data.role || 'cashier', active: data.active ?? true, pin: data.pin, createdAt: data.createdAt, source: 'pos_users' });
          }
        });
      } catch (e) { console.warn('pos_users:', e); }

      // 2. Drivers
      try {
        const snap = await getDocs(collection(db, 'drivers'));
        snap.forEach((d) => {
          const data = d.data();
          const email = data.email || '';
          if (email && !seenEmails.has(email.toLowerCase())) {
            seenEmails.add(email.toLowerCase());
            userList.push({ id: d.id, name: data.name || email, email, role: 'driver', active: data.is_active ?? true, createdAt: data.created_at, source: 'drivers' });
          }
        });
      } catch (e) { console.warn('drivers:', e); }

      // 3. Admin users
      try {
        const snap = await getDocs(collection(db, 'admin_users'));
        snap.forEach((d) => {
          const data = d.data();
          const email = data.email || '';
          if (email && !seenEmails.has(email.toLowerCase())) {
            seenEmails.add(email.toLowerCase());
            userList.push({ id: d.id, name: data.name || `${data.first_name || ''} ${data.last_name || ''}`.trim() || email, email, role: data.role || 'admin', active: !(data.disabled ?? false), createdAt: data.created_at, source: 'admin_users' });
          }
        });
      } catch (e) { console.warn('admin_users:', e); }

      userList.sort((a, b) => a.name.localeCompare(b.name));
      setUsers(userList);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleOpenDialog = (user?: UserRecord) => {
    if (user) {
      setEditingUser(user);
      setFormData({ name: user.name, email: user.email, role: user.role, active: user.active, pin: user.pin || '' });
    } else {
      setEditingUser(null);
      setFormData({ name: '', email: '', role: 'cashier', active: true, pin: '' });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.email.trim()) {
      setSnackbar({ open: true, message: 'Please fill all required fields', severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      const db = getFirestore();
      const isDriver = formData.role === 'driver';
      const userId = editingUser?.id || `user_${Date.now()}`;
      if (isDriver) {
        await setDoc(doc(db, 'drivers', userId), {
          name: formData.name.trim(), email: formData.email.trim(), is_active: formData.active,
          is_available: false, status: 'offline', vehicle_type: 'car', phone: '',
          deliveries_today: 0, total_deliveries: 0, rating: 5.0,
          updated_at: new Date().toISOString(),
          ...(editingUser ? {} : { created_at: new Date().toISOString() }),
        }, { merge: true });
      } else {
        await setDoc(doc(db, 'pos_users', userId), {
          name: formData.name.trim(), email: formData.email.trim(), role: formData.role,
          active: formData.active, pin: formData.pin,
          updatedAt: new Date().toISOString(),
          ...(editingUser ? {} : { createdAt: new Date().toISOString() }),
        }, { merge: true });
      }
      if (editingUser?.source) {
        const wasDriver = editingUser.source === 'drivers';
        if (wasDriver && !isDriver) try { await deleteDoc(doc(db, 'drivers', editingUser.id)); } catch {}
        else if (!wasDriver && isDriver) try { await deleteDoc(doc(db, 'pos_users', editingUser.id)); } catch {}
      }
      setSnackbar({ open: true, message: editingUser ? 'User updated' : 'User created', severity: 'success' });
      setDialogOpen(false);
      loadUsers();
    } catch (error) {
      console.error('Save error:', error);
      setSnackbar({ open: true, message: 'Error saving user', severity: 'error' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    try {
      const db = getFirestore();
      await deleteDoc(doc(db, userToDelete.source || 'pos_users', userToDelete.id));
      setSnackbar({ open: true, message: 'User deleted', severity: 'success' });
      setDeleteConfirmOpen(false); setUserToDelete(null); loadUsers();
    } catch (error) {
      console.error('Delete error:', error);
      setSnackbar({ open: true, message: 'Error deleting user', severity: 'error' });
    }
  };

  // Stats
  const driverCount = users.filter(u => u.role === 'driver').length;
  const staffCount = users.filter(u => u.role !== 'driver' && u.role !== 'customer').length;
  const filteredUsers = filterRole === 'all' ? users : users.filter(u => u.role === filterRole);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress sx={{ color: '#00C896' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, pb: { xs: 4, md: 3 }, maxWidth: '100%' }}>

      {/* ── Header (matching Permissions page) ──────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button startIcon={<BackIcon />} onClick={() => navigate('/settings/program/users')}
            sx={{ color: '#e0e0e0 !important', backgroundColor: '#2a2d42 !important', borderRadius: '8px',
              px: 2.5, py: 1, textTransform: 'none', fontSize: '0.9rem', fontWeight: 500,
              '&:hover': { backgroundColor: '#33364d !important' } }}>
            {t('settings.back') || 'Back'}
          </Button>
          <PersonAddIcon sx={{ fontSize: 28, color: '#9ca3af' }} />
          <Box>
            <Typography sx={{ fontSize: '1.3rem', fontWeight: 600, color: '#ffffff !important' }}>
              {t('settings.user_management') || 'User Management'}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#6b7280 !important' }}>
              {users.length} users · {driverCount} drivers · {staffCount} staff
            </Typography>
          </Box>
        </Box>
        <Button startIcon={<AddIcon />} onClick={() => handleOpenDialog()}
          sx={{ backgroundColor: '#00C896 !important', color: '#1a1d2e !important', borderRadius: '8px',
            px: 3, py: 1, textTransform: 'none', fontSize: '0.9rem', fontWeight: 600,
            '&:hover': { backgroundColor: '#7bc912 !important' } }}>
          {t('settings.add_user') || 'Add User'}
        </Button>
      </Box>

      {/* Filter chips */}
      <Box sx={{ display: 'flex', gap: 0.5, mb: 2, flexWrap: 'wrap' }}>
        {[
          { key: 'all', label: `All (${users.length})` },
          { key: 'driver', label: `Drivers (${driverCount})` },
          { key: 'cashier', label: 'Cashiers' },
          { key: 'admin', label: 'Admins' },
          { key: 'waiter', label: 'Waiters' },
          { key: 'cook', label: 'Cooks' },
        ].map(f => (
          <Chip key={f.key} label={f.label} size="small" onClick={() => setFilterRole(f.key)}
            sx={{ height: 26, fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
              backgroundColor: filterRole === f.key ? '#00C896 !important' : '#2a2d42 !important',
              color: filterRole === f.key ? '#1a1d2e !important' : '#9ca3af !important',
              border: filterRole === f.key ? 'none' : '1px solid #3a3d52',
              '&:hover': { backgroundColor: filterRole === f.key ? '#a5f020 !important' : '#33364d !important' },
            }} />
        ))}
      </Box>

      {/* ── Table (matching Permissions page style) ────────── */}
      {/* Users Table */}
      <TableContainer sx={{ backgroundColor: '#1e2139 !important', borderRadius: '12px', border: '1px solid #3a3d52', overflow: 'auto', maxHeight: 'calc(100vh - 220px)' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {['', t('settings.name') || 'Name', t('settings.email') || 'Email', t('settings.role') || 'Role',
                  'Source', t('settings.status') || 'Status', t('settings.actions') || 'Actions'].map((h, i) => (
                  <TableCell key={i} align={i === 6 ? 'center' : 'left'}
                    sx={{
                      backgroundColor: '#1a1d2e !important', color: '#e0e0e0 !important',
                      fontWeight: 600, fontSize: '0.85rem',
                      borderBottom: '2px solid #3a3d52 !important',
                      ...(i === 0 ? { width: 50, padding: '10px 8px' } : {}),
                      ...(i === 5 ? { width: 90 } : {}),
                      ...(i === 6 ? { width: 100 } : {}),
                      ...(i === 4 ? { width: 90 } : {}),
                      ...(i === 3 ? { width: 130 } : {}),
                    }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6, borderBottom: 'none !important' }}>
                    <PersonAddIcon sx={{ fontSize: 48, color: '#3a3d52', mb: 1 }} />
                    <Typography sx={{ color: '#6b7280 !important', fontSize: '0.9rem' }}>
                      {t('settings.no_users') || 'No users found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user, idx) => {
                  const rc = getRoleConfig(user.role);
                  return (
                    <TableRow key={user.id} sx={{
                      backgroundColor: idx % 2 === 0 ? '#1e2139 !important' : '#222540 !important',
                      '&:hover': { backgroundColor: '#2a2d4a !important' },
                    }}>
                      <TableCell sx={{ borderBottom: '1px solid #2a2d3e !important', padding: '8px' }}>
                        <Avatar sx={{ width: 32, height: 32, fontSize: '0.72rem', fontWeight: 700, mx: 'auto',
                          backgroundColor: `${rc.color}25`, color: rc.color, border: `1.5px solid ${rc.color}40` }}>
                          {user.name.charAt(0).toUpperCase()}
                        </Avatar>
                      </TableCell>
                      <TableCell sx={{ color: '#e0e0e0 !important', fontWeight: 500, fontSize: '0.85rem', borderBottom: '1px solid #2a2d3e !important' }}>
                        {user.name}
                      </TableCell>
                      <TableCell sx={{ color: '#9ca3af !important', fontSize: '0.82rem', borderBottom: '1px solid #2a2d3e !important' }}>
                        {user.email}
                      </TableCell>
                      <TableCell sx={{ borderBottom: '1px solid #2a2d3e !important' }}>
                        <Chip icon={rc.icon} label={rc.label} size="small"
                          sx={{ height: 24, fontSize: '0.7rem', fontWeight: 600,
                            backgroundColor: `${rc.color}18 !important`, color: `${rc.color} !important`,
                            border: `1px solid ${rc.color}35`, '& .MuiChip-icon': { color: `${rc.color} !important` } }} />
                      </TableCell>
                      <TableCell sx={{ borderBottom: '1px solid #2a2d3e !important' }}>
                        <Typography sx={{ fontSize: '0.72rem', color: '#6b7280 !important', textTransform: 'uppercase', fontWeight: 500 }}>
                          {user.source === 'drivers' ? 'Drivers' : user.source === 'admin_users' ? 'Admin' : 'POS'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: '1px solid #2a2d3e !important' }}>
                        <Chip label={user.active ? 'Active' : 'Inactive'} size="small"
                          sx={{
                            height: 22, fontSize: '0.7rem', fontWeight: 500,
                            backgroundColor: user.active ? 'rgba(76,175,80,0.15) !important' : 'rgba(244,67,54,0.15) !important',
                            color: user.active ? '#4CAF50 !important' : '#F44336 !important',
                          }} />
                      </TableCell>
                      <TableCell align="center" sx={{ borderBottom: '1px solid #2a2d3e !important' }}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleOpenDialog(user)}
                            sx={{ color: '#64B5F6 !important', mr: 0.5 }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => { setUserToDelete(user); setDeleteConfirmOpen(true); }}
                            sx={{ color: '#ef5350 !important' }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

      {/* ─── Add/Edit Dialog ─────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, backgroundColor: '#252838', border: '1px solid #3a3d52' } }}>
        <DialogTitle sx={{ color: '#e0e0e0', fontWeight: 700, borderBottom: '1px solid #3a3d52' }}>
          {editingUser ? 'Edit User' : 'Add User'}
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField label="Name" value={formData.name} required fullWidth
              onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
              sx={{ '& .MuiInputLabel-root': { color: '#9ca3af' }, '& .MuiOutlinedInput-root': { backgroundColor: '#2d3048', color: '#e0e0e0' } }} />
            <TextField label="Email" value={formData.email} required fullWidth type="email"
              onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
              sx={{ '& .MuiInputLabel-root': { color: '#9ca3af' }, '& .MuiOutlinedInput-root': { backgroundColor: '#2d3048', color: '#e0e0e0' } }} />
            <FormControl fullWidth>
              <InputLabel sx={{ color: '#9ca3af' }}>Role</InputLabel>
              <Select value={formData.role} label="Role"
                onChange={(e) => setFormData(p => ({ ...p, role: e.target.value }))}
                sx={{ backgroundColor: '#2d3048', color: '#e0e0e0' }}
                MenuProps={{ PaperProps: { sx: { backgroundColor: '#2d3048' } } }}>
                {ROLE_OPTIONS.filter(r => r.value !== 'customer').map(r => (
                  <MenuItem key={r.value} value={r.value} sx={{ color: '#e0e0e0' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ color: r.color }}>{r.icon}</Box>
                      {r.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {formData.role !== 'driver' && (
              <TextField label="PIN (optional)" value={formData.pin} fullWidth type="password"
                placeholder="4-digit PIN" inputProps={{ maxLength: 4 }}
                onChange={(e) => setFormData(p => ({ ...p, pin: e.target.value }))}
                sx={{ '& .MuiInputLabel-root': { color: '#9ca3af' }, '& .MuiOutlinedInput-root': { backgroundColor: '#2d3048', color: '#e0e0e0' } }} />
            )}
            <FormControlLabel
              control={<Switch checked={formData.active} onChange={(e) => setFormData(p => ({ ...p, active: e.target.checked }))}
                sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#00C896' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#00C896' } }} />}
              label={<Typography sx={{ color: '#e0e0e0', fontSize: '0.9rem' }}>Active</Typography>}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #3a3d52' }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: '#9ca3af', textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}
            sx={{ backgroundColor: '#00C896', color: '#1a1d2e', borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 600,
              '&:hover': { backgroundColor: '#7bc912' }, '&:disabled': { opacity: 0.6 } }}>
            {saving ? '...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Delete Dialog ───────────────────────────────────── */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, backgroundColor: '#252838', border: '1px solid #3a3d52' } }}>
        <DialogTitle sx={{ color: '#e0e0e0', fontWeight: 700 }}>Delete User</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#9ca3af' }}>
            Are you sure you want to delete <strong style={{ color: '#e0e0e0' }}>{userToDelete?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteConfirmOpen(false)} sx={{ color: '#9ca3af', textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleDelete}
            sx={{ backgroundColor: '#F44336', color: '#fff', borderRadius: 2, textTransform: 'none', fontWeight: 600,
              '&:hover': { backgroundColor: '#d32f2f' } }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(p => ({ ...p, open: false }))}
          sx={{ backgroundColor: snackbar.severity === 'success' ? '#1b5e20' : '#b71c1c', color: '#fff' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagementPage;
